import { useSearchParams } from "react-router-dom";
import { useBooleanFlagValue } from "@openfeature/react-sdk";

import { useFetchPermissions } from "../services/admin-settings/admin-settings";

const adminSettingsSearchParams = {
  open: "adminSettingsOpen",
  section: "adminSettingsSection",
} as const;

const adminSettingsOpenValue = "true";

export const adminSettingsPermissions = {
  rolesAndPermissions: "edit:user.permission",
  companyKnowledgeView: "view:company-config",
  companyKnowledgeEdit: "edit:company-config",
} as const;

export function hasCompanyKnowledgePermissions(permissions: Array<string>) {
  return (
    permissions.includes(adminSettingsPermissions.companyKnowledgeView) &&
    permissions.includes(adminSettingsPermissions.companyKnowledgeEdit)
  );
}

export function hasAnyAdminSettingsPermission(
  permissions: Array<string>,
  options: { isCompanyKnowledgeEnabled: boolean }
) {
  return (
    permissions.includes(adminSettingsPermissions.rolesAndPermissions) ||
    (options.isCompanyKnowledgeEnabled && hasCompanyKnowledgePermissions(permissions))
  );
}

export type AdminSettingsControls = {
  closeAdminSettings: () => void;
  isAdminSettingsOpen: boolean;
  isAdminSettingsVisible: boolean;
  openAdminSettings: () => void;
  selectedSection: string;
  setAdminSettingsSection: (mode: string) => void;
  hasAdminSettingsPermission: (permissions: Array<string>) => boolean;
};

export function useAdminSettingsNavigation(): AdminSettingsControls {
  const [searchParams, setSearchParams] = useSearchParams();
  const isAdminSettingsVisible = useBooleanFlagValue("admin_settings", false);
  const isCompanyKnowledgeEnabled = useBooleanFlagValue("hr_company_knowledge", false);
  const isAdminSettingsOpen =
    isAdminSettingsVisible &&
    searchParams.get(adminSettingsSearchParams.open) === adminSettingsOpenValue;
  const isUsersManagementEnabled = useBooleanFlagValue("ats_users_management", false);

  // The Users tab should be reachable from any product route via ATS eligibility, so
  // the header button's visibility must also account for it in addition to current product
  // permissions passed into hasAdminSettingsPermission by each app layout. Gated by the same flag as
  // canAccessUsersTab so the button doesn't appear when the tab itself wouldn't.
  // This hook mounts app-wide (AppResolver.tsx), so this fetch is unconditional once both flags are on
  //
  // Note: useFetchAdminSettingsPermissions has staleTime: Infinity, so it
  // resolves to one request per session (shared via the same query key used elsewhere)
  const { hasPermission: hasAtsPermission } = useFetchPermissions(
    "ats",
    isAdminSettingsVisible && isUsersManagementEnabled
  );

  const selectedSection =
    (searchParams.get(adminSettingsSearchParams.section) as string | null) ?? "products";

  const openAdminSettings = () => {
    if (!isAdminSettingsVisible) {
      return;
    }

    const newSearchParams = new URLSearchParams(searchParams);

    newSearchParams.set(adminSettingsSearchParams.open, adminSettingsOpenValue);
    setSearchParams(newSearchParams);
  };

  const setAdminSettingsSection = (section: string) => {
    const newSearchParams = new URLSearchParams(searchParams);

    newSearchParams.set(adminSettingsSearchParams.open, "true");
    newSearchParams.set(adminSettingsSearchParams.section, section);

    setSearchParams(newSearchParams);
  };

  const hasAdminSettingsPermission: AdminSettingsControls["hasAdminSettingsPermission"] = (
    permissions
  ) =>
    hasAnyAdminSettingsPermission(permissions, { isCompanyKnowledgeEnabled }) ||
    (isUsersManagementEnabled && hasAtsPermission(adminSettingsPermissions.rolesAndPermissions));

  const closeAdminSettings = () => {
    const newSearchParams = new URLSearchParams(searchParams);

    newSearchParams.delete(adminSettingsSearchParams.open);
    setSearchParams(newSearchParams, { replace: true });
  };

  return {
    closeAdminSettings,
    isAdminSettingsOpen,
    isAdminSettingsVisible,
    openAdminSettings,
    selectedSection,
    setAdminSettingsSection,
    hasAdminSettingsPermission,
  };
}
